import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, onValue, remove, set } from 'firebase/database';
import { Scheduler, WeekView, Appointments, Toolbar } from '@devexpress/dx-react-scheduler-material-ui';
import { ViewState } from '@devexpress/dx-react-scheduler';
import { Paper, Container, Box, Typography, List, ListItem, ListItemIcon, ListItemText, Checkbox, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import Navbar from './Navbar';
import EventDialog from './EventDialog';

import fc from '../firebaseConfig';

const db = getDatabase(fc);

const getEvents = (callback) => {
  const eventsRef = ref(db, 'events');
  onValue(eventsRef, (snapshot) => {
    const data = snapshot.val();
    const events = data ? Object.entries(data).map(([id, event]) => ({ ...event, id })) : [];
    callback(events);
  });
};

const getEmployees = (callback) => {
  const employeesRef = ref(db, 'employees');
  onValue(employeesRef, (snapshot) => {
    const data = snapshot.val();
    const employees = data ? Object.entries(data).map(([id, employee]) => ({ ...employee, id })) : [];
    callback(employees);
  });
};

const removeOldEvents = () => {
  const eventsRef = ref(db, 'events');
  const fourteenDaysAgo = new Date();
  fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 365000);

  onValue(eventsRef, (snapshot) => {
    const data = snapshot.val();
    if (data) {
      Object.entries(data).forEach(([id, event]) => {
        if (new Date(event.endDate) < fourteenDaysAgo) {
          remove(ref(db, `events/${id}`)).catch((error) => {
            console.error("Error removing old event: ", error);
          });
        }
      });
    }
  });
};

const deleteEvent = (eventId, callback) => {
  const eventRef = ref(db, `events/${eventId}`);
  remove(eventRef).then(() => {
    getEvents(callback);
  }).catch((error) => {
    console.error("Error removing event: ", error);
  });
};

const AppointmentComponent = ({ children, style, data, ...restProps }) => {
  const [hovered, setHovered] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSave = (updatedEvent) => {
    data.onSave(updatedEvent);
    setDialogOpen(false);
  };

  const handleDelete = () => {
    data.onDelete(data.id);
    setDialogOpen(false);
  };

  const tooltipContent = (
    <>
      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'black' }}>
        {data.title}
      </Typography>
      <Typography variant="body2">
        {new Date(data.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}&nbsp;-&nbsp;
        {new Date(data.endDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </Typography>
      <Typography variant="body2">
        Klient: {data.name}, {data.phone}
      </Typography>
      <Typography variant="body2">
        Pracownik: {data.employeeName}, {data.employeeNumber}
      </Typography>
    </>
  );

  return (
    <>
      <Tooltip title={tooltipContent} arrow placement="top">
        <Appointments.Appointment
          {...restProps}
          style={{
            ...style,
            backgroundColor: hovered ? '#505050' : data.color,
            borderRadius: '8px',
            transition: 'background-color 0.3s ease',
            position: 'relative',
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={handleOpenDialog}
        >
          {children}
        </Appointments.Appointment>
      </Tooltip>

      <EventDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        event={data}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </>
  );
};

const CalendarWeek = () => {
  const [events, setEvents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('Week');
  const [isEmployeeDrawerOpen, setIsEmployeeDrawerOpen] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('LOGGED_IN');
    if (!isLoggedIn) {
      navigate('/');
      return;
    }

    removeOldEvents();
    getEvents(setEvents);
    getEmployees((data) => {
      setEmployees(data);
      setSelectedEmployees(data.map(emp => emp.id));
    });

    const intervalId = setInterval(() => {
      if (localStorage.getItem('SAVE') === 'true') {
        setSnackbarMessage('Poprawnie zaktualizowano usługę');
        setOpenSnackbar(true);
        localStorage.removeItem('SAVE');
      }
      if (localStorage.getItem('DELETE') === 'true') {
        setSnackbarMessage('Poprawnie usunięto usługę');
        setOpenSnackbar(true);
        localStorage.removeItem('DELETE');
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [navigate]);

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleDelete = (eventId) => {
    deleteEvent(eventId, setEvents);
  };

  const handleSave = (updatedEvent) => {
    const eventRef = ref(db, `events/${updatedEvent.id}`);
    const { id, title, startDate, endDate, color, employeeName, employeeNumber, name, phone, clientId, employeeId } = updatedEvent;
    const cleanEvent = { title, startDate, endDate, color, employeeName, employeeNumber, name, phone, clientId, employeeId };

    set(eventRef, cleanEvent).then(() => {
      getEvents(setEvents);
    }).catch((error) => {
      console.error("Error saving event: ", error);
    });
  };

  const handleEmployeeChange = (employeeId, isChecked) => {
    setSelectedEmployees((prev) =>
      isChecked ? [...prev, employeeId] : prev.filter(id => id !== employeeId)
    );
  };

  const handleDateChange = (value) => {
    if (value) {
      setCurrentDate(new Date(value));
    } else {
      setCurrentDate(new Date()); // Fallback to today's date if input is cleared
    }
  };

  const filteredEvents = events.filter(event => {
    const employee = employees.find(emp => emp.name === event.employeeName);
    return selectedEmployees.includes(employee?.id);
  });

  const formattedEvents = filteredEvents.map((event) => ({
    ...event,
    startDate: event.startDate ? new Date(event.startDate).toISOString() : '',
    endDate: event.endDate ? new Date(event.endDate).toISOString() : '',
    onDelete: handleDelete,
    onSave: handleSave,
  }));

  return (
    <>
      <Navbar />
      <Container maxWidth={false} disableGutters sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'auto' }}>
          <Paper elevation={3} sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'center' }}>
              <IconButton
                onClick={() => setCurrentDate(prevDate => {
                  const newDate = new Date(prevDate);
                  newDate.setDate(newDate.getDate() - 7);
                  return newDate;
                })}
              >
                <ChevronLeft style={{ fontSize: '3rem' }} />
              </IconButton>
              <input
                type="date"
                value={currentDate.toISOString().split('T')[0]}
                onChange={(e) => handleDateChange(e.target.value)}
                style={{ textAlign: 'center', border: 'none', fontSize: '1.5rem', maxWidth: '16rem', margin: '0 8px' }}
              />
              <IconButton
                onClick={() => setCurrentDate(prevDate => {
                  const newDate = new Date(prevDate);
                  newDate.setDate(newDate.getDate() + 7);
                  return newDate;
                })}
              >
                <ChevronRight style={{ fontSize: '3rem' }} />
              </IconButton>
            </Box>
            <Typography variant="h6">
              <a
                href="/Calendar"
                style={{
                  color: 'black',
                  fontSize: '25px',
                  textDecoration: 'underline',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = 'blue'}
                onMouseLeave={(e) => e.target.style.color = 'black'}
              >
                Widok Dzienny
              </a>
            </Typography>
          </Paper>

          <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
            <Box
              sx={{
                position: 'fixed',
                top: '63%',
                left: 0,
                transform: 'translateY(-50%)',
                height: '100%',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box
                onMouseEnter={() => setIsEmployeeDrawerOpen(true)}
                sx={{
                  width: 20,
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  height: '100%',
                  zIndex: 11,
                }}
              >
                <ChevronRight />
              </Box>

              <Box
                onMouseLeave={() => setIsEmployeeDrawerOpen(false)}
                sx={{
                  width: isEmployeeDrawerOpen ? '250px' : '0px',
                  transition: 'width 0.3s ease',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  backgroundColor: '#ffffff',
                  borderRight: isEmployeeDrawerOpen ? '4px solid #00' : 'none',
                  height: '100%',
                  position: 'fixed',
                  left: 0,
                  zIndex: 10,
                  top: 0,
                  maxHeight: 'calc(100% - 65px)',
                }}
              >
                <Typography variant="h6" sx={{ p: 2, display: isEmployeeDrawerOpen ? 'block' : 'none' }}>Pracownicy</Typography>
                <List sx={{ display: isEmployeeDrawerOpen ? 'block' : 'none' }}>
                  {employees.map((employee) => (
                    <ListItem key={employee.id}>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            backgroundColor: employee.color,
                            borderRadius: '10%',
                            border: '2px solid black'
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary={employee.name} />
                      <Checkbox
                        edge="end"
                        checked={selectedEmployees.includes(employee.id)}
                        onChange={(e) => handleEmployeeChange(employee.id, e.target.checked)}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

            </Box>

            <Box sx={{ flexGrow: 1, overflow: 'hidden', marginTop: '-2rem' }}>
              <Paper>
                <Scheduler data={formattedEvents} locale="pl-PL">
                  <ViewState
                    currentDate={currentDate}
                    onCurrentDateChange={setCurrentDate}
                    currentViewName={viewMode}
                    onCurrentViewNameChange={setViewMode}
                  />
                  <Toolbar />
                  <WeekView startDayHour={6} endDayHour={23} />
                  <Appointments appointmentComponent={AppointmentComponent} />
                </Scheduler>
              </Paper>
            </Box>

          </Box>
        </Box>
      </Container>
      
      <Snackbar open={openSnackbar} autoHideDuration={2000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CalendarWeek;
